import { Objects } from "@salaxy/core";

import HbsRuntime from "handlebars/runtime";
import { hbsTemplates } from "../model";
import { hbsHelperT } from "./helpers/hbsHelperT";
import { hbsHelperD } from "./helpers/hbsHelperD";
import { hbsHelperR } from "./helpers/hbsHelperR";
import { hbsHelperP } from "./helpers/hbsHelperP";
import { hbsHelperC } from "./helpers/hbsHelperC";
import { hbsHelperJson } from "./helpers/hbsHelperJson";
import { hbsHelperU } from "./helpers/hbsHelperU";
import { hbsHelperSum } from "./helpers/hbsHelperSum";
import { hbsHelperSub } from "./helpers/hbsHelperSub";
import { hbsHelperDiv } from "./helpers/hbsHelperDiv";
import { hbsHelperMul } from "./helpers/hbsHelperMul";
/**
 * Provides helper methods for Handlebars templating.
 */
export class Templates {

  /**
   * Creates a new instance of the service.
   */
  constructor() {
    this.registerHelpers();
  }

  /**
   * Gets the HTML using the Handlebars template.
   * @param templateName Name of the template to render.
   * @param data Data for the template.
   * @param lang Translation language
   * @returns HTML as string
   */
  public getHtml(templateName: string, data?: any, lang = "fi"): string {
    if (!hbsTemplates[templateName]) {
      throw new Error(`Cannot find report template: ${templateName || "null"}. See debug for list of registered templates.`);
    }
    const translationHelperOverDrive = (key: string, options: any) => hbsHelperT(lang, key, options);
    return hbsTemplates[templateName](data, { allowProtoPropertiesByDefault: true, helpers: { t: translationHelperOverDrive } });
  }

  /**
   * Gets HTML from provided templateContent and data
   * @param templateContent Template as handlebars HTML / string.
   * @param data Data for the rendering.
   * @param lang Translation language
   * @returns HTML as string
   */
  public getHtmlDynamic(templateContent: string, data?: any, lang = "fi"): string {
    const tokensRegex = /\{\{\s*\(\s*(\S+)\s*\(\s*\}\}|\{\{\s*(\S+)\s*\}\}|\{\{\s*>\s*(\S+)\s*\}\}|\{\{\s*([t|d|r|p|c])\s+(.*)\s*\}\}/g;
    return templateContent.replace(tokensRegex, (_unused, subVarName: string, varName, partial, helperName: string, content: string) => {
      // simple tags
      if (subVarName ?? varName) {
        return this.readValue(data, subVarName ?? varName, "");
      } else if (partial) {
        // partials, additional context not supported
        return this.getHtml(partial, data, lang);
      } else {
        // helpers
        return this.runHelper(data, helperName, content, "", lang);
      }
    });
  }

  /**
   * Register helpers.
   */
  private registerHelpers(): void {
    HbsRuntime.registerHelper("t", (key: string, options: any) => hbsHelperT("en", key, options));
    HbsRuntime.registerHelper("d", (start: string | number, options: any) => hbsHelperD(start, options));
    HbsRuntime.registerHelper("r", (value: number, options: any) => hbsHelperR(value, options));
    HbsRuntime.registerHelper("p", (value: string | number, options: any) => hbsHelperP(value, options));
    HbsRuntime.registerHelper("c", (value: number, options: any) => hbsHelperC(value, options));
    HbsRuntime.registerHelper("json", (value: string) => hbsHelperJson(value));
    HbsRuntime.registerHelper("u", (value: string) => hbsHelperU(value));
    HbsRuntime.registerHelper("sum", (...values) => hbsHelperSum(...values));
    HbsRuntime.registerHelper("sub", (value1, value2) => hbsHelperSub(value1, value2));
    HbsRuntime.registerHelper("div", (value1, value2) => hbsHelperDiv(value1, value2));
    HbsRuntime.registerHelper("mul", (...values) => hbsHelperMul(...values));
  }

  private readValue(object: any, path: string, defaultValue: any): any {
    if (!object || !path) {
      return defaultValue;
    }
    const subKeys = path.split(".");
    let value = object;
    for (const subKey of subKeys) {
      if (Objects.has(value, subKey)) {
        value = value[subKey];
      } else {
        return defaultValue;
      }
    }
    return value;
  }

  private runHelper(object: any, helperName: string, content: string, defaultValue: string, lang: string): string {
    const parameters: ({ key: string | null, value: any })[] = this.splitParameters(content);
    parameters.forEach(((x) => {
      x.value = this.readValueOrConstantOrHelper(object, x.value, defaultValue, lang);
    }));
    const args = this.getHelperArgs(parameters);
    if (helperName === "t") {
      const translationHelperOverDrive = (key: string, options: any) => hbsHelperT(lang, key, options);
      return translationHelperOverDrive(args.first, args.options) as string;
    } else {
      return HbsRuntime.helpers[helperName].call(this, args.first, args.options);
    }
  }

  private readValueOrConstant(object: any, path: string, defaultValue: any): any {
    if (
      path &&
      (
        (path.startsWith("\"") && path.endsWith("\""))
        ||
        (path.startsWith("'") && path.endsWith("'"))
      )) {
      return path.slice(1, path.length - 1);
    }
    return this.readValue(object, path, defaultValue);
  }

  private readValueOrConstantOrHelper(object: any, content: string, defaultValue: any, lang: string): any {
    const valueRegex = /\(\s*([t|d|r|p|c])\s+(.*)\s*\)|\(\s*(\S+)\s*\)|(\s*(\S+)\s*)/g;
    return content.replace(valueRegex, (_unused, subHelperName: string, subContent: string, subVarName: string, varName: string) => {
      if (subHelperName) {
        return this.runHelper(object, subHelperName, subContent, defaultValue, lang);
      } else {
        return this.readValueOrConstant(object, subVarName ?? varName, defaultValue);
      }
    });
  }

  private getHelperArgs(parameters: ({ key: string | null, value: any })[]) {
    return {
      first: parameters.length ? parameters[0].value : null,
      options: this.getHelperOptions(parameters)
    };
  }

  private getHelperOptions(parameters: ({ key: string | null, value: any })[]) {
    const options: any = {};
    for (const parameter of parameters) {
      if (parameter.key) {
        options.hash = options.hash ?? {};
        options.hash[parameter.key] = parameter.value;
      }
    }
    return options;
  }

  private splitParameters(content: string): ({ key: string | null, value: any })[] {
    const splitRegex = /((\S+)=\((\s*([t|d|r|p|c| ])*\s*\S+)\))|((\S+)=(\S+))|(\S+)/g;
    const matches = content.match(splitRegex);
    if (!matches) {
      return [];
    }
    return matches.map((r) => {
      const kv = r.split("=");
      if (kv.length === 2) {
        return { key: kv[0], value: kv[1] };
      }
      return { key: null, value: kv[0] };
    });
  }
}
